import { useMemo, useState } from "react";
import { styled } from "@mui/material/styles";
import {
  Box,
  IconButton,
  Badge,
  Typography,
  Drawer,
  Tooltip,
} from "@mui/material";
import {
  NotificationsOutlined as NotificationIcon,
  SettingsOutlined as SettingsIcon,
  PeopleOutlined as PeopleIcon,
  LogoutOutlined as LogoutIcon,
} from "@mui/icons-material";
import { Link, useNavigate } from "react-router";
import PropTypes from "prop-types";
import Breadcrumbs from "@accredo/fe-shared/components/Breadcrumbs.jsx";
import { useAtomValue, useSetAtom } from "jotai";
import { notificationAtom } from "../../atoms/notificationAtom.js";
import NotificationPopover from "../../components/NotificationPopover.jsx";
import { useUser } from "@accredo/fe-shared/hooks/user.js";
import { useLogout } from "@accredo/fe-shared/hooks/useLogout.js";
import UserMenu from "@accredo/fe-shared/components/UserMenu.jsx";
import { colorsSemantic } from "@accredo/fe-shared/theme/index.js";
import MenuIcon from "@mui/icons-material/Menu";
import Sidebar from "./Sidebar.jsx";
import { useMediaQuery, useTheme } from "@mui/material";
import logo_src from "@accredo/fe-shared/assets/logo.svg";

function getUnreadCount(notifications) {
  if (!Array.isArray(notifications)) {
    return 0;
  }

  return notifications.filter((notification) => !notification?.seen_at).length;
}

const HeaderContainer = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: 0,
  padding: `${theme.spacing(2)} ${theme.spacing(2)}`,
  paddingLeft: theme.spacing(1),
  backgroundColor: theme.palette.background.paper,
  width: "100%",
  [theme.breakpoints.up("sm")]: {
    padding: `${theme.spacing(2)} ${theme.spacing(4)}`,
    justifyContent: "flex-end",
  },
}));

const UserSection = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  gap: theme.spacing(1.5),
}));

const RoundedIconButton = styled(IconButton)(({ theme }) => ({
  backgroundColor: theme.palette.grey[100],
  borderRadius: "40px",
  padding: theme.spacing(1),
  "&:hover": {
    backgroundColor: theme.palette.grey[200],
  },
  "& .MuiSvgIcon-root": {
    fontSize: "24px",
    color: theme.palette.primary.main,
  },
}));

const StyledBadge = styled(Badge)(({ theme }) => ({
  "& .MuiBadge-badge": {
    backgroundColor: colorsSemantic.icon.accent.gold,
    color: theme.palette.common.white,
    fontSize: "12px",
    fontWeight: 500,
    lineHeight: 1.5,
    minWidth: "22px",
    height: "22px",
    padding: `0 ${theme.spacing(0.75)}`,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    borderRadius: "999px",
  },
}));

const UserAvatar = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  backgroundColor: theme.palette.grey[100],
  borderRadius: "40px",
  padding: theme.spacing(1),
  minWidth: "40px",
  minHeight: "40px",
}));

const AvatarText = styled(Typography)(({ theme }) => ({
  fontSize: "16px",
  fontWeight: 700,
  lineHeight: 1.5,
  color: theme.palette.primary.main,
}));

const UserInfo = styled(Box)({
  display: "flex",
  flexDirection: "column",
  gap: 0,
  paddingTop: 0,
  paddingBottom: "2px",
});

const UserName = styled(Typography)(({ theme }) => ({
  fontSize: "12px",
  fontWeight: 600,
  lineHeight: 1.5,
  color: theme.palette.text.primary,
  marginBottom: "-2px",
}));

const UserRole = styled(Typography)(({ theme }) => ({
  fontSize: "12px",
  fontWeight: 400,
  lineHeight: 1.5,
  color: theme.palette.text.primary,
  marginBottom: "-2px",
}));

const UserContainer = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  gap: theme.spacing(1),
}));

/**
 * Generate initials from a full name
 * @param {string} name - Full name
 * @returns {string} Initials (max 2 characters)
 */
function getInitials(name) {
  if (!name) {
    return "U";
  }

  const parts = name.trim().split(" ").filter(Boolean);
  if (parts.length === 0) return "U";
  if (parts.length === 1) return parts[0].charAt(0).toUpperCase();

  return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase();
}

export default function Header({ onNotificationClick }) {
  const { user, loading: userLoading } = useUser();
  const notifications = useAtomValue(notificationAtom);
  const setNotifications = useSetAtom(notificationAtom);
  const [notificationAnchorEl, setNotificationAnchorEl] = useState(null);
  const [userMenuAnchorEl, setUserMenuAnchorEl] = useState(null);
  const navigate = useNavigate();
  const logoutAndGoToLogin = useLogout();
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up("md"));

  const notificationCount = useMemo(
    () => getUnreadCount(notifications),
    [notifications]
  );

  const handleNotificationButtonClick = (event) => {
    if (onNotificationClick) {
      onNotificationClick(event);
    }

    setNotificationAnchorEl(event.currentTarget);
  };

  const handleNotificationPopoverClose = () => {
    setNotificationAnchorEl(null);
  };

  const handleUserMenuOpen = (event) => {
    setUserMenuAnchorEl(event.currentTarget);
  };

  const handleUserMenuClose = () => {
    setUserMenuAnchorEl(null);
  };

  const handleLogout = async () => {
    await logoutAndGoToLogin();
    handleUserMenuClose();
  };

  const handleSettingsClick = () => {
    navigate("/account");
    handleUserMenuClose();
  };

  const handleSubstitutionsClick = () => {
    navigate("/substitutions");
    handleUserMenuClose();
  };

  const userMenuItems = [
    {
      label: "Einstellungen",
      icon: SettingsIcon,
      onClick: handleSettingsClick,
    },
    {
      label: "Stellvertretungen",
      icon: PeopleIcon,
      onClick: handleSubstitutionsClick,
    },
    {
      label: "Abmelden",
      icon: LogoutIcon,
      onClick: handleLogout,
    },
  ];

  const user_name =
    String(user?.vorname + " " + user?.nachname).trim() || "Vorname Name";
  const userRole = user?.kundenname || "Verwalter";
  const userInitials = getInitials(user_name);

  const [drawerOpen, setDrawerOpen] = useState(false);

  return (
    <HeaderContainer>
      {!isDesktop && (
        <>
          {/* Burger-Icon nur auf kleinen Screens */}
          <IconButton
            sx={{ display: { md: "none" } }}
            onClick={() => setDrawerOpen(true)}
          >
            <MenuIcon />
          </IconButton>

          {/* Logo */}
          <Box
            component={Link}
            to="/home"
            sx={{
              display: { md: "none" },
              ml: 1,
              mr: "auto",
              height: 40,
              alignItems: "center",
              textDecoration: "none",
            }}
          >
            <img src={logo_src} alt="Logo" style={{ height: 40 }} />
          </Box>

          {/* Drawer mit Sidebar */}
          <Drawer
            anchor="left"
            open={drawerOpen}
            onClose={() => setDrawerOpen(false)}
            sx={{ display: { md: "none" } }}
          >
            <Sidebar logoSrc={logo_src} onToggle={() => {}} />
          </Drawer>
        </>
      )}

      {isDesktop && <Breadcrumbs />}

      <UserSection>
        <StyledBadge
          badgeContent={notificationCount}
          invisible={notificationCount === 0}
        >
          <RoundedIconButton
            onClick={handleNotificationButtonClick}
            aria-label="notifications"
          >
            <NotificationIcon />
          </RoundedIconButton>
        </StyledBadge>

        <UserContainer sx={{ cursor: "pointer" }} onClick={handleUserMenuOpen}>
          <UserAvatar>
            <AvatarText>{userInitials}</AvatarText>
          </UserAvatar>
          <UserInfo>
            <UserName>{user_name}</UserName>
            <Tooltip title={userRole} arrow>
              <UserRole
                sx={{
                  maxWidth: 120,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                  display: "block",
                }}
              >
                {userRole}
              </UserRole>
            </Tooltip>
          </UserInfo>
        </UserContainer>
      </UserSection>
      <NotificationPopover
        anchorEl={notificationAnchorEl}
        onClose={handleNotificationPopoverClose}
        bellNotifications={notifications}
        setBellNotifications={setNotifications}
      />

      {/* User Menu */}
      <UserMenu
        anchorEl={userMenuAnchorEl}
        open={Boolean(userMenuAnchorEl)}
        onClose={handleUserMenuClose}
        menuItems={userMenuItems}
      />
    </HeaderContainer>
  );
}

Header.propTypes = {
  onNotificationClick: PropTypes.func,
};
